import React, { forwardRef, HtmlHTMLAttributes, memo, ReactNode } from "react";
import classNames from "classnames";
import { RefType } from "../../types";
import { useIsMobileView } from "../../utils/useIsMobileHook";
import { Breadcrumbs, BreadcrumbsPropsType } from "../Breadcrumbs";
import { BackButton } from "../Breadcrumbs/BackButton";

import styles from "./styles.module.scss";

export type PageHeaderProps = HtmlHTMLAttributes<HTMLDivElement> & {
  ref?: RefType<HTMLDivElement>
  text?: ReactNode
  type?: "simple" | "bannerSimple"
  actions?: ReactNode | ReactNode[]
  className?: string
  supportingText?: ReactNode
  breadcrumbsProps?: BreadcrumbsPropsType
  additionalActions?: ReactNode | ReactNode[]
  breadcrumbsClassname?: string,
  headerContainerClassname?: string,
  contentClassname?: string,
  headerClassname?: string,
  actionsClassname?: string,
  additionalActionsClassname?: string,
  divider?: boolean,
};

export const PageHeader = memo<PageHeaderProps>(forwardRef((
  {
    type = "simple",
    text,
    actions,
    children,
    className,
    supportingText,
    breadcrumbsProps,
    headerContainerClassname,
    additionalActions,
    breadcrumbsClassname,
    contentClassname,
    headerClassname,
    actionsClassname,
    additionalActionsClassname,
    divider = false,
    ...props
  },
  ref,
) => {
  const isMobile = useIsMobileView();
  return (
    <div
      ref={ref}
      aria-label="page-header"
      className={classNames(
        className,
        styles.root,
        styles[type],
      )}
      {...props}
    >
      {type === "bannerSimple" && <div className={styles.gradient}></div>}
      <div className={classNames(styles.container, headerContainerClassname, { [styles.divider]: divider })}>
        <div className={styles.breadcrumbsContainer}>
          {
            isMobile
              ? <BackButton />
              : <Breadcrumbs
                {...breadcrumbsProps}
                className={classNames(breadcrumbsClassname, styles.breadcrumbs)}
              />
          }
        </div>
        {
          (!!text || !!supportingText || !!actions || !!additionalActions) &&
          <div
            className={classNames(styles.content, contentClassname)}
            style={{
              gridTemplateColumns: !isMobile
                ? `1fr ${[ actions, additionalActions ].filter(Boolean)
                  .map(_ => "auto")
                  .join(" ")}`
                : `1fr ${[ additionalActions ].filter(Boolean)
                  .map(_ => "auto")
                  .join(" ")}` ,
            }}
          >
            <div className={classNames(styles.textBlock, headerClassname)}>
              {typeof text === "string" ? <h1 className={styles.text}>{text}</h1> : text}
              {supportingText && <p className={styles.supportingText}>{supportingText}</p>}
            </div>
            {
              actions &&
              <div className={classNames(styles.actions, actionsClassname)}>
                {actions}
              </div>
            }
            {
              additionalActions &&
              <div className={classNames(styles.additionalActions, additionalActionsClassname)}>
                {additionalActions}
              </div>
            }
          </div>
        }
      </div>
      {children}
    </div>
  );
}));
